export interface ResourceChatPreview { id: string; title: string; providerName: string; city?: string; stateCodes: string[]; regionIds: number[]; phone?: string; website?: string }
export interface ResourceChatChoice { id: string; title: string; providerName: string }
export interface ResourceConversationChoice { id: number; name: string }
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function resourceReferenceId(href: string, origin = window.location.origin): string | null {
  try {
    const url = new URL(href, origin);
    if (url.origin !== origin || url.search || url.hash) return null;
    const match = url.pathname.match(/^\/resources\/([^/]+)$/);
    return match && uuid.test(match[1]) && !/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(match[1]) ? match[1].toLowerCase() : null;
  } catch { return null; }
}
const resourceReferenceLabel = (title?: string) => {
  const value = title?.trim() ?? '';
  return value && value.length <= 300 && !Array.from(value).some(character => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127 || (code >= 0x202a && code <= 0x202e) || (code >= 0x2066 && code <= 0x2069);
  }) ? value : 'Resource';
};
export function resourceReferenceContent(id: string, title?: string) {
  if (!uuid.test(id) || /^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(id)) throw new Error('Invalid resource reference.');
  return { type: 'text', text: resourceReferenceLabel(title), marks: [{ type: 'link', attrs: { href: `/resources/${id.toLowerCase()}`, target: '_self', rel: 'noopener noreferrer' } }] };
}
export function resourceIdsInDoc(doc: unknown): string[] {
  const ids = new Set<string>();
  const visit = (value: any, depth: number) => {
    if (depth > 32 || !value || typeof value !== 'object') return;
    for (const mark of value.marks ?? []) {
      if (mark.type !== 'link') continue;
      const id = resourceReferenceId(mark.attrs?.href ?? '');
      if (id && ids.size < 20) ids.add(id);
    }
    for (const child of value.content ?? []) visit(child, depth + 1);
  };
  visit(doc, 0); return Array.from(ids);
}
export function appendResourceToDraft(doc: unknown, plaintext: string, id: string, title?: string): { doc: unknown; plaintext: string } {
  let root: any = doc;
  try { if (typeof root === 'string') root = JSON.parse(root); } catch { root = null; }
  if (root?.doc) root = root.doc;
  const existing = root?.type === 'doc' && Array.isArray(root.content)
    ? root.content : (plaintext ? [{ type: 'paragraph', content: [{ type: 'text', text: plaintext }] }] : []);
  if (resourceIdsInDoc({ type: 'doc', content: existing }).includes(id.toLowerCase())) return { doc: { type: 'doc', content: existing }, plaintext };
  return { doc: { type: 'doc', content: [...existing, { type: 'paragraph', content: [resourceReferenceContent(id, title)] }] }, plaintext: `${plaintext}${plaintext ? '\n' : ''}${resourceReferenceLabel(title)}` };
}
async function request<T>(path: string, token: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/resource-directory/chat/${path}`, { headers: { Authorization: `Bearer ${token}` }, signal, cache: 'no-store' });
  if (!response.ok) throw new Error('Resource unavailable.');
  return response.json();
}
export const resourceChat = {
  search: (token: string, query: string, signal?: AbortSignal) => request<ResourceChatChoice[]>(`search?query=${encodeURIComponent(query)}`, token, signal),
  preview: (token: string, id: string, signal?: AbortSignal) => request<ResourceChatPreview>(encodeURIComponent(id), token, signal),
  conversations: (token: string, signal?: AbortSignal, conversationId?: number) => request<ResourceConversationChoice[]>(`share-conversations${conversationId ? `?conversationId=${encodeURIComponent(conversationId)}` : ''}`, token, signal),
};
